import type { CustomTableLayout, TableCell, TDocumentDefinitions } from 'pdfmake/interfaces.js';
import type { ReportTable } from './screens/reports/reports.js';
import { reportWorkbook } from './screens/reports/xlsx.js';
import { TOKENS } from './tokens.js';

/**
 * Tedarikçi ekstresi (ADR 0119 §5): ofiste ve tedarikçi portalında **aynı** belge. İstemcide
 * üretilir (pdfmake, exceljs; ADR 0040 #189 kalıbı): sunucuya iş düşmez. Tutarlar kanonik ondalık
 * metindir (`"1200.50"`); biçim dile göre, kayan noktaya çevrilmeden gruplanır. Metinler çağırandan
 * gelir (iki uygulamanın i18n'i ayrıdır).
 */

export interface StatementEntryView {
  date: string;
  kind: 'invoice' | 'payment' | 'reversal';
  reference: string;
  dueDate: string | null;
  debit: string;
  credit: string;
  balance: string;
}

export interface StatementView {
  currency: string;
  from: string;
  to: string;
  opening: string;
  closing: string;
  totals: { debit: string; credit: string };
  entries: StatementEntryView[];
}

export interface StatementLabels {
  title: string;
  /** "Buyer" / "Supplier" satırları. */
  buyer: string;
  supplier: string;
  period: string;
  currency: string;
  printed: string;
  opening: string;
  closing: string;
  totals: string;
  date: string;
  type: string;
  reference: string;
  due: string;
  debit: string;
  credit: string;
  balance: string;
  kinds: Record<StatementEntryView['kind'], string>;
  empty: string;
  footer: string;
}

export interface StatementDocument {
  statement: StatementView;
  buyerName: string;
  supplierName: string;
  printedAt: string;
  language: string;
  labels: StatementLabels;
}

/**
 * Kanonik ondalık metni dile göre biçimler (`1234567.5` → `1,234,567.50` / `1.234.567,50`):
 * ayırıcılar `Intl`'den, gruplama metin üzerinde; büyük tutarda kayan nokta hatası yok.
 */
export function formatDecimal(value: string, language: string): string {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return value;
  let group = ',';
  let decimal = '.';
  try {
    for (const part of new Intl.NumberFormat(language).formatToParts(1234567.5)) {
      if (part.type === 'group') group = part.value;
      if (part.type === 'decimal') decimal = part.value;
    }
  } catch {
    // Bilinmeyen dil: İngilizce ayırıcılar
  }
  const integer = (match[2] ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, group);
  const fraction = (match[3] ?? '').padEnd(2, '0').slice(0, 2);
  return `${match[1]}${integer}${decimal}${fraction}`;
}

/** Gün (`YYYY-MM-DD`) dile göre kısa biçim; UTC gün olarak okunur. */
export function formatStatementDay(day: string, language: string): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return day;
  return new Intl.DateTimeFormat(language, {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};

/** Ekstrenin PDF tanımı: A4 dikey; taraflar, dönem, açılış, hareketler yürüyen bakiyeyle, kapanış. */
export function supplierStatementPdf(input: StatementDocument): TDocumentDefinitions {
  const { statement: s, labels: l, language } = input;
  const money = (value: string) => formatDecimal(value, language);
  const day = (value: string) => formatStatementDay(value, language);
  const right = { alignment: 'right' as const };
  const body: TableCell[][] = [
    [l.date, l.type, l.reference, l.due, l.debit, l.credit, l.balance].map((text, index) => ({
      text,
      bold: true,
      ...(index >= 4 ? right : {}),
    })),
    [
      { text: day(s.from) },
      { text: l.opening, colSpan: 5, italics: true },
      {},
      {},
      {},
      {},
      { text: money(s.opening), ...right, bold: true },
    ],
    ...s.entries.map((entry) => [
      { text: day(entry.date) },
      { text: l.kinds[entry.kind] },
      { text: entry.reference },
      { text: entry.dueDate ? day(entry.dueDate) : '' },
      { text: entry.debit === '0.00' ? '' : money(entry.debit), ...right },
      { text: entry.credit === '0.00' ? '' : money(entry.credit), ...right },
      { text: money(entry.balance), ...right },
    ]),
    [
      { text: '' },
      { text: l.totals, colSpan: 3, bold: true },
      {},
      {},
      { text: money(s.totals.debit), ...right, bold: true },
      { text: money(s.totals.credit), ...right, bold: true },
      { text: money(s.closing), ...right, bold: true },
    ],
  ];
  return {
    pageSize: 'A4',
    pageMargins: [36, 40, 36, 48],
    info: { title: `${l.title} ${input.supplierName} ${s.currency}`, creator: 'Felixmate' },
    defaultStyle: { fontSize: 8.5, color: TOKENS['navy-900'] },
    footer: (current, total) => ({
      columns: [
        { text: l.footer, color: TOKENS['text-secondary'] },
        { text: `${current} / ${total}`, alignment: 'right', color: TOKENS['text-secondary'] },
      ],
      margin: [36, 12, 36, 0],
      fontSize: 7.5,
    }),
    content: [
      { text: l.title, fontSize: 15, bold: true, color: TOKENS['navy-900'], margin: [0, 0, 0, 8] },
      {
        table: {
          widths: [90, '*'],
          body: [
            [{ text: l.buyer, bold: true }, input.buyerName],
            [{ text: l.supplier, bold: true }, input.supplierName],
            [{ text: l.period, bold: true }, `${day(s.from)} – ${day(s.to)}`],
            [{ text: l.currency, bold: true }, s.currency],
            [{ text: l.printed, bold: true }, input.printedAt],
          ],
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 10],
      },
      {
        table: { headerRows: 1, widths: [52, 48, '*', 52, 62, 62, 66], body },
        layout: LINES,
      },
      ...(s.entries.length === 0
        ? [
            {
              text: l.empty,
              italics: true,
              margin: [0, 8, 0, 0] as [number, number, number, number],
            },
          ]
        : []),
      {
        text: `${l.closing}: ${money(s.closing)} ${s.currency}`,
        bold: true,
        alignment: 'right',
        margin: [0, 10, 0, 0],
      },
    ],
  };
}

/** Ekstrenin Excel dosyası: tarih gerçek tarih, tutarlar sayı hücresi (süzülür, toplanır). */
export function supplierStatementWorkbook(input: StatementDocument): Promise<Uint8Array> {
  const { statement: s, labels: l } = input;
  const amount = (value: string) => (value === '0.00' ? null : Number(value));
  const table = {
    report: 'supplier_statement',
    columns: [
      { key: 'date', kind: 'date', label: l.date },
      { key: 'type', kind: 'text', label: l.type },
      { key: 'reference', kind: 'text', label: l.reference },
      { key: 'due', kind: 'date', label: l.due },
      { key: 'debit', kind: 'money', label: l.debit },
      { key: 'credit', kind: 'money', label: l.credit },
      { key: 'balance', kind: 'money', label: l.balance },
    ],
    rows: [
      [s.from, l.opening, null, null, null, null, Number(s.opening)],
      ...s.entries.map((entry) => [
        entry.date,
        l.kinds[entry.kind],
        entry.reference,
        entry.dueDate,
        amount(entry.debit),
        amount(entry.credit),
        Number(entry.balance),
      ]),
      [
        s.to,
        l.closing,
        null,
        null,
        Number(s.totals.debit),
        Number(s.totals.credit),
        Number(s.closing),
      ],
    ],
  } as unknown as ReportTable;
  return reportWorkbook(table, {
    title: `${l.title} ${s.currency}`,
    period: `${input.supplierName} · ${input.buyerName} · ${s.from} – ${s.to}`,
    generated: `${l.printed}: ${input.printedAt}`,
  });
}

/** Dosya adı: `statement-<tedarikçi>-<para birimi>-<başlangıç>_<bitiş>.<uzantı>`. */
export function statementFileName(supplierName: string, s: StatementView, extension: string) {
  const slug = supplierName
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return `statement-${slug || 'supplier'}-${s.currency}-${s.from}_${s.to}.${extension}`;
}
